// src/pages/customer/Categories.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { getCategories } from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import { FiArrowRight, FiGrid, FiSearch, FiX } from "react-icons/fi";
import leafBg from "../../assests/background.png";

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const c = await getCategories();
      setCategories(c);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return categories;
    return categories.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [categories, search]);

  return (
    <div className="min-h-screen bg-[#f7f6f2]">

      <Helmet>
        <title>Shop by Category | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
      </Helmet>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1a4d2e] via-[#2d6a4f] to-[#1b6b3a] py-16 px-4 text-center text-white">
        {/* subtle dot pattern */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        {/* decorative blobs */}
        <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 rounded-full bg-white/5" />
        <div className="pointer-events-none absolute -bottom-12 -right-12 w-64 h-64 rounded-full bg-white/5" />

        <div className="relative z-10">
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase bg-white/15 border border-white/20 px-4 py-1.5 rounded-full mb-5 text-white/90">
            <FiGrid className="w-3 h-3" />
            Tripura Agro Collections
          </span>

          <h1 className="font-display text-3xl md:text-5xl font-bold mb-3 leading-tight tracking-tight" style={{ fontFamily: "var(--custom-body-font-family)" }}>
            Shop by Category
          </h1>
          <p className="text-white/70 max-w-md mx-auto text-sm md:text-base">
            Farm-fresh produce, spices, oils &amp; more — sourced directly from Tripura's finest farms.
          </p>

          {/* search bar inside hero */}
          <div className="mt-8 max-w-md mx-auto relative">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search categories…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-10 py-3 rounded-xl bg-white text-gray-800 text-sm placeholder:text-gray-400 outline-none shadow-lg focus:ring-2 focus:ring-white/40"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* count pill */}
          {!loading && (
            <div className="mt-5 inline-flex items-center gap-1.5 bg-white/10 border border-white/15 px-4 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-medium text-white/80">
                {filtered.length} {filtered.length === 1 ? "category" : "categories"} available
              </span>
            </div>
          )}
        </div>
      </section>

      {/* ── Grid Section ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div>

        {loading ? (
          <Spinner center />
        ) : categories.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 bg-green-50 rounded-3xl flex items-center justify-center text-4xl mx-auto mb-5">📦</div>
            <h3 className="text-lg font-bold text-gray-700 mb-2">No categories yet</h3>
            <p className="text-gray-400 text-sm">Check back soon for new arrivals.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center text-4xl mx-auto mb-5">🔍</div>
            <h3 className="text-lg font-bold text-gray-700 mb-2">No results for "{search}"</h3>
            <button
              onClick={() => setSearch("")}
              className="text-sm text-green-700 font-medium mt-2 hover:underline"
            >
              Clear search
            </button>
          </div>
        ) : (
          <>
            {/* section label row */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-0.5">
                  Browse All
                </p>
                <h2 className="text-xl font-bold text-gray-800">
                  {search ? `Results for "${search}"` : "All Categories"}
                </h2>
              </div>
              <span className="text-xs text-gray-400 bg-white border border-gray-200 px-3 py-1.5 rounded-full">
                {filtered.length} categories
              </span>
            </div>

            {/* ── Cards Grid ── */}
            <div className="relative overflow-hidden">
              {/* Decorative leaf branch background — sits behind the category cards only */}
              <img
                src={leafBg}
                alt=""
                aria-hidden="true"
                className="absolute bottom-0 -left-16 w-[420px] sm:w-[560px] pointer-events-none select-none"
                style={{ opacity: 0.12, zIndex: 0 }}
              />
              <div className="relative grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" style={{ zIndex: 1 }}>
              {filtered.map((cat, i) => (
                <Link
                  key={cat.id}
                  to={`/products?category=${cat.id}`}
                  className="group relative bg-white rounded-2xl overflow-hidden border border-gray-100 flex flex-col
                             transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-green-900/10
                             hover:border-green-200"
                  style={{ animationDelay: `${i * 35}ms` }}
                >
                  {/* top green accent bar */}
                  <div className="h-[3px] w-full bg-gradient-to-r from-[#2d6a4f] to-[#52b788] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />

                  {/* ── Image ── */}
                  <div className="relative overflow-hidden aspect-square bg-gradient-to-br from-green-50 to-emerald-50">
                    {cat.image ? (
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-5xl">
                        🌿
                      </div>
                    )}

                    {/* dark gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent
                                    opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* product count badge — top left */}
                    {cat.productCount != null && (
                      <div className="absolute top-2.5 left-2.5 bg-black/40 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded-full
                                      opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {cat.productCount} items
                      </div>
                    )}

                    {/* animated arrow badge — bottom right */}
                    <div className="absolute bottom-2.5 right-2.5 w-7 h-7 rounded-full bg-white flex items-center justify-center
                                    shadow-md opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0
                                    transition-all duration-300">
                      <FiArrowRight className="w-3.5 h-3.5 text-[#2d6a4f]" />
                    </div>
                  </div>

                  {/* ── Label ── */}
                  <div className="px-3.5 py-3.5 flex flex-col gap-0.5">
                    <h3 className="font-semibold text-gray-800 text-[13px] leading-snug group-hover:text-[#2d6a4f] transition-colors duration-200">
                      {cat.name}
                    </h3>
                    {cat.productCount != null ? (
                      <p className="text-[11px] text-gray-400">
                        {cat.productCount} products
                      </p>
                    ) : (
                      <p className="text-[11px] text-gray-400">View all →</p>
                    )}
                  </div>
                </Link>
              ))}
              </div>
            </div>

            {/* ── Bottom CTA ── */}
            <div className="mt-14">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a4d2e] to-[#2d6a4f] px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
                {/* dot pattern */}
                <div
                  className="pointer-events-none absolute inset-0 opacity-[0.06]"
                  style={{
                    backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
                    backgroundSize: "22px 22px",
                  }}
                />
                <div className="relative z-10 text-center sm:text-left">
                  <p className="text-white font-bold text-lg mb-1">Can't find what you need?</p>
                  <p className="text-white/60 text-sm">Browse our entire product collection.</p>
                </div>
                <Link
                  to="/products"
                  className="relative z-10 inline-flex items-center gap-2 bg-white text-[#1a4d2e] font-semibold text-sm
                             px-6 py-3 rounded-xl hover:bg-green-50 transition-colors duration-200 whitespace-nowrap flex-shrink-0"
                >
                  Browse All Products
                  <FiArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
}